// =============================================================================
// Forgot Password Page - Sarsen Capital Partners Investor Portal
// Design: Sarsen "Strata" identity (2026-09) via the shared AuthShell layout
// =============================================================================

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CodeInput } from '../../components/ui';
import { authService } from '../../services/auth.service';
import { getApiError } from '../../services/api';
import {
  AuthShell,
  AuthHeading,
  AuthError,
  AuthButton,
  AuthLink,
  AuthCheckItem,
  AuthSuccessBadge,
  authInputClass,
  authInputIconButtonClass,
  authQuietButtonClass,
} from '../../components/layout/AuthShell';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// =============================================================================
// Types
// =============================================================================

type Step = 'email' | 'code' | 'password' | 'success';

// =============================================================================
// Component
// =============================================================================

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is account activation mode (from "Activate your account" link)
  const isActivationMode = searchParams.get('mode') === 'activate';

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // ===========================================================================
  // Password Validation
  // ===========================================================================

  const passwordChecks = [
    { check: password.length >= 12, text: 'At least 12 characters' },
    { check: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { check: /[a-z]/.test(password), text: 'One lowercase letter' },
    { check: /[0-9]/.test(password), text: 'One number' },
    { check: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password), text: 'One special character' },
  ];

  const isPasswordValid = passwordChecks.every(c => c.check);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.requestPasswordReset(email);

      if (result.success) {
        setStep('code');
        startCountdown();
      } else {
        setError(result.error || 'Failed to send code');
      }
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (verificationCode: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.verifyPasswordResetCode(email, verificationCode);

      if (result.success && result.resetToken) {
        setResetToken(result.resetToken);
        setError(null); // Clear any previous error before transitioning
        setStep('password');
      } else {
        setError(result.error || 'Invalid code');
        setCode('');
      }
    } catch (err) {
      setError(getApiError(err).message);
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid) {
      setError('Please fix password requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.resetPassword(email, resetToken, password);

      if (result.success) {
        setStep('success');
      } else {
        setError(result.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setError(null);
    setIsLoading(true);

    try {
      await authService.requestPasswordReset(email);
      startCountdown();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleBack = () => {
    if (step === 'code') {
      setStep('email');
      setCode('');
    } else if (step === 'password') {
      setStep('code');
      setPassword('');
      setConfirmPassword('');
    }
    setError(null);
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <AuthShell caption={isActivationMode ? 'Account Activation' : 'Password Reset'}>
      {/* Step: Email */}
      {step === 'email' && (
        <div>
          <AuthHeading
            eyebrow={isActivationMode ? 'Account Activation' : 'Password Reset'}
            title={isActivationMode ? 'Activate your account' : 'Reset your password'}
            subtitle={
              isActivationMode
                ? 'Enter your email address to activate your investor portal account.'
                : 'Enter your email to receive a verification code.'
            }
          />

          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                autoComplete="email"
                autoFocus
                className={authInputClass}
              />
            </div>

            {error && <AuthError message={error} />}

            <AuthButton type="submit" disabled={isLoading}>
              {isLoading ? 'Sending…' : (isActivationMode ? 'Activate account' : 'Send code')}
            </AuthButton>
          </form>

          <div className="mt-10 pt-6 border-t border-brand-line">
            {isActivationMode ? (
              <p className="m-0 text-sm text-brand-ink">
                Already have an account? <AuthLink to="/login">Sign in</AuthLink>
              </p>
            ) : (
              <AuthLink to="/login">Back to sign in</AuthLink>
            )}
          </div>
        </div>
      )}

      {/* Step: Verification Code */}
      {step === 'code' && (
        <div>
          <AuthHeading
            eyebrow="Verification"
            title="Check your email"
            subtitle={
              <>
                We sent a 6-digit verification code to{' '}
                <span className="font-medium text-brand-navy">{email}</span>
              </>
            }
          />

          <div className="space-y-6">
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={handleVerifyCode}
              error={!!error}
              disabled={isLoading}
            />

            {error && <AuthError message={error} center />}

            {isLoading && (
              <p className="m-0 text-sm text-brand-muted text-center">Verifying…</p>
            )}

            <div className="flex flex-col items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={countdown > 0 || isLoading}
                className={authQuietButtonClass}
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-brand-muted hover:text-brand-navy transition-colors"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step: New Password */}
      {step === 'password' && (
        <div>
          <AuthHeading
            eyebrow={isActivationMode ? 'Account Activation' : 'Password Reset'}
            title={isActivationMode ? 'Create your password' : 'Create a new password'}
            subtitle={
              isActivationMode
                ? 'Choose a strong password to secure your investor portal account.'
                : 'Enter a strong password for your account.'
            }
          />

          <form onSubmit={handleResetPassword} className="space-y-4">
            {/* New Password Input */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New Password"
                required
                autoComplete="new-password"
                autoFocus
                className={`${authInputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={authInputIconButtonClass}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Password Requirements */}
            <div className="bg-white border border-brand-line p-4">
              <p className="m-0 mb-3 text-[11px] font-semibold tracking-[.2em] uppercase text-brand-muted">
                Password must contain
              </p>
              <ul className="m-0 p-0 list-none text-xs space-y-2">
                {passwordChecks.map(({ check, text }) => (
                  <AuthCheckItem key={text} met={check} text={text} />
                ))}
              </ul>
            </div>

            {/* Confirm Password Input */}
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                className={`${authInputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={authInputIconButtonClass}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Password Match Status */}
            {confirmPassword.length > 0 && (
              <p className={`m-0 text-xs ${passwordsMatch ? 'text-brand-navy' : 'text-brand-terracotta'}`}>
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}

            {error && <AuthError message={error} />}

            <AuthButton
              type="submit"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
            >
              {isLoading
                ? (isActivationMode ? 'Activating…' : 'Resetting…')
                : (isActivationMode ? 'Complete activation' : 'Reset password')}
            </AuthButton>

            <div className="text-center">
              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-brand-muted hover:text-brand-navy transition-colors"
              >
                Back
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div>
          <AuthSuccessBadge />

          <AuthHeading
            center
            title={isActivationMode ? 'Account activated' : 'Password reset'}
            subtitle={
              isActivationMode
                ? 'Your account is now active. You can sign in to access your investor portal.'
                : 'Your password has been changed. You can now sign in with your new password.'
            }
          />

          <AuthButton type="button" onClick={() => navigate('/login')}>
            Sign in
          </AuthButton>
        </div>
      )}
    </AuthShell>
  );
}

export default ForgotPasswordPage;
